import React, { CSSProperties, forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"
import { Input } from "baseui/input"
import { colors, css, glyphs, images } from "../../Styles"
import { ListStyle_Types, CreateListStyle, FieldTypes, CreateFieldTypes, FieldStyles, CreateFieldStyle } from "../../Definitions"

// #region | InputField Component
// InputField Component
interface InputField_Props {
  keyPass?: string
  context?:string | number
  placeholder?:string
  fieldStyle?: FieldStyles | string
  fieldType?: FieldTypes | string
  inputType?:string
  startEnhancer?:string | JSX.Element
  endEnhancer?:string
  textStart?: boolean
  isClearable?: boolean
  isRequired?:boolean
  inTable?:boolean
  disabled?:boolean
  onChange?:(e:string)=>void
  onBlur?: () => void
  overrides?:{
    startEnhancer?:CSSProperties
    endEnhancer?:CSSProperties
  }
  maxValue?: number,
  minValue?: number
}

export interface InputField_Handles {
  setError: (state?:boolean) => void,
  focus: () => void,
  context: string,
  setContext: (e:string) => void,
}
/**
 * InputField
 * @param placeholder text in inputfield before typing.
 * @param fieldStyle color styling for inputfield, e.g. fieldStyle="blue".
 * @param fieldType width of inputfield, e.g. "fieldType="shortField".
 * @param inputType type of inputfield, e.g. "password" or "email" etc.
 * @param startEnhancer start icon.
 * @param endEnhancer end icon.
 * @param textStart if true the text will move to the left where the startenhancer would have been.
 * @param isClearable if true a "clear icon" will appear in the end of the field if the field has a value.
 * @param isRequired if true the field is required to be filled before moving on.
 * @param overrides style overrides.
 * @returns a BaseUi input field, build from content param. 
 */
const _InputField:React.ForwardRefRenderFunction<InputField_Handles, InputField_Props> = ({ maxValue, minValue, keyPass, placeholder, fieldStyle, fieldType, inputType, startEnhancer, endEnhancer, textStart = false, isClearable = false, isRequired = false, disabled=false, inTable=false, overrides, context, onChange, onBlur }: InputField_Props, ref) => {
  const initialValue = context !== undefined ? String(context) : "";
  const [value, setValue] = useState(initialValue);
  const _inputRef = useRef<any>(undefined)
  const [isFocused, setIsFocused] = useState(false)
  const [isError, setError] = useState(false)
  const [required, setRequired] = useState(false)
  
  // const isNumeric = (num: any) => (typeof(num) === 'number' || (typeof(num) === "string" && num.trim() !== '' && !isNaN(+num)));

  useImperativeHandle(ref, ()=>({
    setError(state=true) {
      setError(state)
    },
    focus() {
      if (_inputRef&&_inputRef.current) _inputRef.current.focus()
    },
    context: value,
    setContext(e:string) { setValue(e) }
  }))

  // Colors from fieldStyle
  const { backgroundColor, hoverColor, fontStyle } = useMemo(() => {
    var _Style = CreateFieldStyle(fieldStyle)
    return _Style
  }, [fieldStyle])

  // Width from fieldType
  const { width, height, textpadding, distancePaddingInput, enhancerPadding } = useMemo(() => {
    var _Style = CreateFieldTypes(fieldType)
    return _Style
  }, [fieldType])

  // Handle padding and margin based on the presence of the startEnhancer
  const handleDistance = () => {
    if (startEnhancer) {
      return { padding: "0px", margin: "0px 0px 0px -5px" } // with startEnhancer
    } else if (!startEnhancer && textStart) {
      // return { padding: "0px", margin: "0px 0px 0px -22px" } // without startEnhancer but textStart=true
      return { padding: "0px", margin: "0px" }
    } else {
      return { padding: distancePaddingInput } // without startEnhancer and textStart=false
    }
  }
  
  const EditField = () => {
  if (inTable) {
    return { margin: "0px 0px 0px -5px" }
    } else {
      return { margin: "0px" }
    }
  }

  return (
    <Input disabled={disabled}
      max={maxValue}
      min={minValue}
      placeholder={placeholder}
      startEnhancer={() => !startEnhancer? (<></>) : (
        startEnhancer === "string" ? 
          <img alt="start_icn" src={startEnhancer} style={overrides?.startEnhancer?overrides.startEnhancer: {
          ...css.inputFieldEnhancer}}/>
        :
         <>{startEnhancer}</>   
      )}
      endEnhancer={() => endEnhancer?<img alt="end_icn" src={endEnhancer} style={overrides?.endEnhancer?overrides.endEnhancer: { 
        ...css.inputFieldEnhancer}}/> : (<></>) 
      }
      clearable={isClearable}
      required={isRequired}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        if (onBlur) {
          onBlur()
        }
      }}
      error={isError} 
      inputRef={_inputRef}
      clearOnEscape
      value={value}
      type={inputType}
      onChange={e => {
        setError(false)
        setValue(e.target.value)
        if (onChange) onChange(e.target.value)
      }}

      // onChange={e => {
      //   setError(false)
      //   if (onChange) {
      //     if (inputType === "number" && (!isNumeric(e.target.value) || e.target.value === "")) {
      //       setError(true);
      //     }
      //     onChange(e.target.value);
      //   }
      // }}

      // style overrides
      overrides={{
        Root: { style: () => Object.assign({}, {
          ...backgroundColor,
          width: width,
          height: height,
          padding: textpadding,
          transition: "0.1s",
          outline: isFocused ? `2px solid ${isError?colors.systemRed:colors.greyLightTertiary}` : isError?colors.systemRed:"none",
          borderRadius: "10px",
          border: "none",
          ...EditField()
        } as React.CSSProperties )},   
        Input: { style: () => Object.assign({}, { 
          ...fontStyle,
          filter: disabled ? "opacity(0.3)" : "opacity(1)",
          padding: textpadding,
          transition: "0.1s",
          '::placeholder': { ...fontStyle, opacity:"30%" }
        } as React.CSSProperties )},
        InputContainer: { style: () => Object.assign({}, {
          ...backgroundColor,
          ...handleDistance()
        } as React.CSSProperties )}, 
        EndEnhancer: { style: () => Object.assign({}, {
          ...backgroundColor,
          padding: enhancerPadding
        } as React.CSSProperties )},
        StartEnhancer: { style: () => Object.assign({}, {
          ...backgroundColor,
          padding: enhancerPadding
        } as React.CSSProperties )},
        ClearIcon: { style: () => Object.assign({}, {
          width: "18px !important",
          height: "18px !important"
        } as React.CSSProperties )},
        ClearIconContainer: { style: () => Object.assign({}, {
          padding: "0px",
          margin: "0px -17px 0px 0px",
          zIndex: "9999"
        } as React.CSSProperties )},
        MaskToggleHideIcon: () => <div style={{cursor: "pointer"}}><glyphs.PasswordHide size="25px"/></div>,
        MaskToggleShowIcon: () => <div style={{cursor: "pointer"}}><glyphs.PasswordShow size="25px"/></div>
      }}
    />
  );
}
const InputField = forwardRef(_InputField)
export default InputField
// #endregion

// TODO: Hvad er det her? kommentar tak
// const useRefs = () => {
//   const refsByKey = useRef<Record<string,HTMLElement | null>>({})
//   const setRef = (element: HTMLElement | null, key: string) => {
//     refsByKey.current[key] = element;
//   }
//   return {refsByKey: refsByKey.current, setRef};
// }

// #region | InputFieldList Component
interface InputFieldList_Props {
  placeholder: string[]
  fieldStyle?: string
  fieldType?: string
  inputTypes?: string[]
  startEnhancers?: (string | JSX.Element)[];
  endEnhancers?: string[];
  textStart?: boolean[];
  isClearable?: boolean[]
  isRequired: boolean[],
  disabled?: boolean[]
  error?:boolean[]
  listStyle?: ListStyle_Types|string,
  listGap?: string
}
export type InputFieldList_PropHandle = {
  setError: (index:number, state:boolean) => void,
  focus: (index:number) => void,
  context: (index:number) => string|undefined
  refs: React.MutableRefObject<InputField_Handles[]>
}
/**
 * InputFieldList
 * @param placeholder text in inputfield before typing.
 * @param fieldStyle color styling for inputfield, e.g. fieldStyle="blue".
 * @param fieldType width of inputfield, e.g. "fieldType="shortField".
 * @param inputType type of inputfield, e.g. "password" or "email" etc.
 * @param startEnhancers start icon.
 * @param endEnhancers end icon.
 * @param textStart if true the text will move to the left where the startenhancer would have been.
 * @param isClearable if true a "clear icon" will appear in the end of the field if the field has a value.
 * @param isRequired if true the field is required to be filled before moving on.
 * @param error shows an error on the input field if it has no value.
 * @param listStyle defines the direction of the InputFields in the List and the seperator between them.
 * @returns A list of input fields with optional enhancers.
*/
const _InputFieldList:React.ForwardRefRenderFunction<InputFieldList_PropHandle, InputFieldList_Props> = ({placeholder, fieldStyle, fieldType, inputTypes, startEnhancers, endEnhancers, textStart, isClearable, isRequired, disabled, error, listStyle, listGap}: InputFieldList_Props, ref) => {
  /*const [elRefs, setElRefs] = React.useState<React.MutableRefObject<HTMLInputElement>[]>([])
  React.useEffect(() => { // add or remove refs
    setElRefs((elRefs) => Array(placeholder.length).fill(undefined).map((_, i) => elRefs[i] || createRef()))
  }, [placeholder])*/
  const _listGap = listGap ? { gap: listGap } : { gap: "5px" };
  const { listDirection } = useMemo(() => CreateListStyle(listStyle), [listStyle]);
  const inputRefs = useRef<InputField_Handles[]>([])

  useImperativeHandle(ref, ()=>({
    setError(index=0,state) { 
      if (inputRefs.current && inputRefs.current[index]) inputRefs.current[index].setError(state)
    },
    focus(index=0) {
      if (inputRefs.current && inputRefs.current[index]) inputRefs.current[index].focus()
    },
    context(index=0) {
      //if (elRefs&&elRefs[index]&&elRefs[index].current) return elRefs[index].current.value
      if (inputRefs.current && inputRefs.current[index]) return inputRefs.current[index].context
      return undefined
    },
    refs:inputRefs
  }))

  const Item_List = useMemo(() => {
    return placeholder.map((_text, context_Index) => {
      // const _fieldstyle = fieldStyle&&fieldStyle[context_Index] ? fieldStyle[context_Index] : undefined
      // const _fieldType = fieldType&&fieldType[context_Index] ? fieldType[context_Index] : undefined
      const _inputType = inputTypes&&inputTypes[context_Index] ? inputTypes[context_Index]:undefined;
      var _startEnhancers = startEnhancers&&startEnhancers[context_Index] ? startEnhancers[context_Index] : undefined
      var _endEnhancers = endEnhancers&&endEnhancers[context_Index] ? endEnhancers[context_Index] : undefined
      const _textstart = textStart&&textStart[context_Index] ? textStart[context_Index] : undefined
      const _isClearable = isClearable&&isClearable[context_Index] ? isClearable[context_Index] : undefined
      const _disabled = disabled&&disabled[context_Index] ? disabled[context_Index] : undefined

      var view = [(
        <InputField key={"List_Input_" + context_Index} ref={(el) => el?inputRefs.current[context_Index] = el:{}}
          placeholder={_text}
          fieldStyle={fieldStyle}
          fieldType={fieldType}
          inputType={_inputType}
          startEnhancer={_startEnhancers}
          endEnhancer={_endEnhancers} 
          textStart={_textstart}
          isClearable={_isClearable}
          isRequired={isRequired[context_Index]}
          disabled={_disabled}
        />
      )]
      // Seperator position based on list style for the list where last item will not have the seperator
      // if (placeholder.length != context_Index + 1) view.push(<div style={seperatorPosition as React.CSSProperties}/>)
      return view
    });
  }, [placeholder, startEnhancers, endEnhancers, inputTypes]);

  return (
    // return of div with list direction based on list style
    <div style={{...listDirection as React.CSSProperties, ..._listGap, width: "fit-content"}}>
      {Item_List}
    </div>
  );
}
export const InputFieldList = forwardRef(_InputFieldList)
// #endregion